import { type Field, fieldLength } from "@ondit/publishing";
import type { DistributionSource } from "~/core/api/distribution";
import { type Locale, translate } from "~/core/i18n";

export function articleMarkdown(source: DistributionSource): string {
	const body = source.blocks
		.map((block) => {
			switch (block.type) {
				case "answer":
					return block.text;
				case "section":
					return `## ${block.heading}\n\n${block.md}`;
				case "faq":
					return block.items.map((item) => `### ${item.q}\n\n${item.a}`).join("\n\n");
				case "table":
					return [
						block.caption ?? "",
						...block.rows.map((row) => row.map((cell, i) => `${block.columns[i]}: ${cell}`).join(" · ")),
					].join("\n\n");
				case "stat":
					return `${block.label}: ${block.value}${block.unit ? ` ${block.unit}` : ""}${block.source_url ? ` (${block.source_url})` : ""}`;
				case "sources":
					return block.items.map((item) => `- [${item.label}](${item.url})`).join("\n");
				case "chart":
					return `${block.title}\n\n${JSON.stringify(block.series)}`;
				// The key survives the trip to a platform; the composer turns it
				// back into an attachment.
				case "image":
					return `![${block.alt}](ondit-file:${block.file_id})${block.caption ? `\n\n${block.caption}` : ""}`;
				case "video":
				case "audio":
					return [block.caption, block.transcript].filter(Boolean).join("\n\n");
			}
			return "";
		})
		.join("\n\n");
	return `# ${source.title}\n\n${body}\n\n${sourceLine(source)}`;
}

/**
 * A short post's first draft, cut to the platform's limits: a title field
 * already carries the title, so the body does not repeat it, and answers are
 * dropped from the end before the one left is shortened. The address is kept
 * whole — a post without it is the page's copy with nothing linking back.
 */
export function shortPost(source: DistributionSource, title?: Field, body?: Field) {
	const answers = source.blocks.flatMap((b) => (b.type === "answer" ? [b.text] : []));
	const lead = title ? [] : [source.title];
	const fits = (f: Field | undefined, v: string) => !f?.max_length || fieldLength(f, v) <= f.max_length;
	const join = (parts: string[]) => [...lead, ...parts, source.canonical].join("\n\n");
	while (answers.length > 1 && !fits(body, join(answers))) answers.pop();
	if (answers.length && !fits(body, join(answers))) answers[0] = clip(answers[0], (v) => fits(body, join([v])), "…");
	return { title: title ? clip(source.title, (v) => fits(title, v)) : "", body: join(answers) };
}

/** The longest start of `text` that fits, ending at a word where the text has spaces. */
function clip(text: string, fits: (v: string) => boolean, tail = ""): string {
	if (fits(text)) return text;
	const chars = Array.from(text);
	let n = chars.length;
	while (n > 0 && !fits(chars.slice(0, n).join("").trimEnd() + tail)) n--;
	const cut = chars.slice(0, n).join("");
	const word = /\s/.test(chars[n] ?? "") ? cut : cut.replace(/\s+\S*$/, "");
	return (word || cut).replace(/[\s,.;:，。；：、—-]+$/, "") + tail;
}

/**
 * The citation every post carries, in the article's language rather than the
 * reader's: it is part of the text that gets pasted into the platform.
 */
function sourceLine(source: DistributionSource): string {
	return `${translate(source.locale as Locale, "distribution.originalArticle")}: ${source.canonical}`;
}
